import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme, neutral, blueGrey, lightBlue } from '../design-system/theme';
import { useNavigate } from 'react-router-dom';
import { AdminShell } from '../components/admin/AdminShell';
import { adminApi } from '../services/adminApi';
import { Template, Project, DataCollector, PaginatedResponse } from '../types/admin';
import { ViewModal, Section, SectionTitle, InfoGrid, InfoCard, InfoLabel, InfoValue, Badge, TagList, Tag } from '../components/admin/ViewModal';
import TemplateBuilder from '../components/admin/TemplateBuilder';
import { DevicePreviewFrame } from '../components/preview/DevicePreviewFrame';
import { TemplatePreview } from '../components/preview/TemplatePreview';
import {
  PageHeader,
  PageTitle,
  FilterGroup,
  SearchInput,
  SelectInput,
  PrimaryButton,
  SecondaryButton,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  IconButton,
  ActionButtons,
  AlertMessage,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
} from '../styles/adminShared';

// Page-specific styled components (template-specific layouts and preview)
const PageTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageSubtitle = styled.p`
  color: ${theme.textSecondary};
  margin: 8px 0 0 0;
  font-size: 14px;
`;

const DataCategoriesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const CategoryTag = styled.span`
  background: ${lightBlue[1]};
  color: ${lightBlue[5]};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
`;

const ContentPreview = styled.div`
  background: ${neutral[1]};
  padding: 20px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  max-height: 300px;
  overflow-y: auto;
  
  h1, h2, h3, h4 { margin-top: 0; color: ${theme.textPrimary}; }
  p { margin: 0 0 12px 0; }
  ul, ol { margin: 0 0 12px 0; padding-left: 24px; }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: ${blueGrey[1]};
  border-radius: 8px;
  margin-bottom: 20px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? theme.primary : theme.textSecondary};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  
  &:hover {
    color: ${theme.primary};
  }
`;

const StatsRowLocal = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const StatPillLocal = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${neutral[1]};
  border: 1px solid ${neutral[3]};
  border-radius: 6px;
  font-size: 13px;
  
  .value {
    font-weight: 700;
    color: ${theme.primary};
  }
  
  .label {
    color: ${theme.textSecondary};
    font-weight: 400;
  }
`;

const FullPageModal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${neutral[2]};
  z-index: 1000;
  overflow-y: auto;
`;

const FullPageHeader = styled.div`
  background: white;
  padding: 16px 24px;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const FullPageBody = styled.div`
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;
`;

const LoadingMessage = styled.div`
  padding: 40px;
  text-align: center;
  color: ${theme.textSecondary};
`;

const TemplateNameCell = styled.strong`
  color: ${theme.textPrimary};
`;

const TemplateDescriptionCell = styled.div`
  font-size: 12px;
  color: ${theme.textSecondary};
  margin-top: 4px;
`;

const DescriptionText = styled.p`
  color: ${theme.textPrimary};
  line-height: 1.6;
`;

const SaveToLibraryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SaveToLibraryInfo = styled.p`
  color: ${theme.textSecondary};
  margin-top: 0;
`;

const FormGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const FormFieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: ${theme.textPrimary};
`;

const FormFieldContainer = styled.div`
  width: 100%;
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 10px 16px;
  border: 2px solid ${theme.border};
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${theme.primary};
  }
`;

export const Templates: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<PaginatedResponse<Template> | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [dataCollectors, setDataCollectors] = useState<DataCollector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Modals
  const [showBuilder, setShowBuilder] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewModalTab, setViewModalTab] = useState<'details' | 'preview'>('details');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [viewingTemplate, setViewingTemplate] = useState<Template | null>(null);
  const [showSaveToLibrary, setShowSaveToLibrary] = useState(false);
  const [saveToLibraryTemplate, setSaveToLibraryTemplate] = useState<Template | null>(null);
  const [saveToLibraryForm, setSaveToLibraryForm] = useState({
    regulation: 'DPDP',
    geography: 'INDIA',
    category: 'GENERAL',
    name: '',
    description: '',
    legalBasis: '',
    complianceNotes: '',
  });
  const [savingToLibrary, setSavingToLibrary] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [statusFilter, searchQuery]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Note: pageSize=10 for projects and dataCollectors (used in dropdowns)
      // If more than 10 items exist, users won't see them in selector
      // TODO: Implement pagination or search for dropdowns with many items
      const [templatesData, projectsData, dataCollectorsData] = await Promise.all([
        adminApi.listTemplates(undefined, 0, 50),
        adminApi.listProjects(1, 10),
        adminApi.listDataCollectors(1, 10),
      ]);
      
      setTemplates(templatesData);
      setProjects(projectsData.data || projectsData.content || []);
      setDataCollectors(dataCollectorsData.data || dataCollectorsData.content || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await adminApi.listTemplates(undefined, 0, 50);
      setTemplates(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load templates');
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowBuilder(true);
  };

  const handleEditTemplate = async (template: Template) => {
    try {
      // Fetch full template details (list might not include consentText)
      const fullTemplate = await adminApi.getTemplate(template.templateId);
      setEditingTemplate(fullTemplate);
      setShowBuilder(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load template for editing');
    }
  };

  const handleViewTemplate = async (template: Template) => {
    try {
      const fullTemplate = await adminApi.getTemplate(template.templateId);
      setViewingTemplate(fullTemplate);
      setShowViewModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load template details');
    }
  };

  const handleSaveTemplate = async (templateData: Partial<Template>) => {
    try {
      if (editingTemplate) {
        await adminApi.updateTemplate(editingTemplate.templateId, templateData);
        setSuccess('Template updated successfully');
      } else {
        await adminApi.createTemplate(templateData);
        setSuccess('Template created successfully');
      }
      setShowBuilder(false);
      loadTemplates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save template');
    }
  };

  const handleToggleStatus = async (template: Template) => {
    try {
      const currentStatus = template.status || 'ACTIVE';
      if (currentStatus === 'ACTIVE') {
        await adminApi.disableTemplate(template.templateId);
        setSuccess('Template disabled');
      } else {
        await adminApi.enableTemplate(template.templateId);
        setSuccess('Template enabled');
      }
      loadTemplates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update template status');
    }
  };

  const handleDuplicateTemplate = async (template: Template) => {
    try {
      // Fetch full template details
      const fullTemplate = await adminApi.getTemplate(template.templateId);
      
      // Create a copy - the backend uses hasPrivacyPolicy, hasConsent, etc. fields
      // Map frontend fields to backend DTO format
      const duplicateData: any = {
        projectId: fullTemplate.projectId,
        dataCollectorId: fullTemplate.dataCollectorId,
        dataCategories: fullTemplate.dataCategories || ['GENERAL'],
        // Use backend document fields if available, otherwise set defaults
        hasPrivacyPolicy: (fullTemplate as any).hasPrivacyPolicy ?? true,
        privacyPolicyDataType: (fullTemplate as any).privacyPolicyDataType || 'text/html',
        privacyPolicyData: (fullTemplate as any).privacyPolicyData || fullTemplate.privacyPolicyUrl || '<p>Privacy Policy</p>',
        hasConsent: (fullTemplate as any).hasConsent ?? true,
        consentDataType: (fullTemplate as any).consentDataType || 'text/html',
        consentData: (fullTemplate as any).consentData || fullTemplate.consentText || '<p>Consent Text</p>',
        hasTermOfService: (fullTemplate as any).hasTermOfService ?? false,
        termOfServiceDataType: (fullTemplate as any).termOfServiceDataType,
        termOfServiceData: (fullTemplate as any).termOfServiceData,
        expiryDays: fullTemplate.expiryDays || 365,
      };
      
      await adminApi.createTemplate(duplicateData);
      setSuccess('Template duplicated successfully');
      loadTemplates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to duplicate template');
    }
  };

  const handleOpenSaveToLibrary = async (template: Template) => {
    try {
      const fullTemplate = await adminApi.getTemplate(template.templateId);
      setSaveToLibraryTemplate(fullTemplate);
      setSaveToLibraryForm({
        regulation: 'DPDP',
        geography: 'INDIA',
        category: 'GENERAL',
        name: fullTemplate.name || fullTemplate.templateName || '',
        description: fullTemplate.description || '',
        legalBasis: '',
        complianceNotes: '',
      });
      setShowSaveToLibrary(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load template');
    }
  };

  const handleSaveToLibrary = async () => {
    if (!saveToLibraryTemplate) return;
    
    if (!saveToLibraryForm.name.trim()) {
      setError('Template name is required');
      return;
    }
    
    setSavingToLibrary(true);
    try {
      await adminApi.saveToLibrary({
        templateId: saveToLibraryTemplate.templateId,
        regulation: saveToLibraryForm.regulation,
        geography: saveToLibraryForm.geography,
        category: saveToLibraryForm.category,
        name: saveToLibraryForm.name,
        description: saveToLibraryForm.description || undefined,
        legalBasis: saveToLibraryForm.legalBasis || undefined,
        complianceNotes: saveToLibraryForm.complianceNotes || undefined,
      });
      setSuccess('Template saved to library successfully');
      setShowSaveToLibrary(false);
      setSaveToLibraryTemplate(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save template to library');
    } finally {
      setSavingToLibrary(false);
    }
  };

  const getProjectName = (projectId?: string) => {
    const project = projects.find(p => p.projectId === projectId);
    return project?.name || 'Unknown Project';
  };

  const getDataCollectorName = (dataCollectorId?: string) => {
    const dc = dataCollectors.find(d => d.dataCollectorId === dataCollectorId);
    return dc?.name || 'Unknown';
  };

  const templateList = templates?.data || templates?.content || [];
  const filteredTemplates = templateList.filter(t => {
    if (statusFilter && t.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        t.name?.toLowerCase().includes(query) ||
        t.templateName?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading && !templates) {
    return (
      <AdminShell title="Templates">
        <LoadingMessage>
          Loading templates...
        </LoadingMessage>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Consent Templates">
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>📋 Consent Templates</PageTitle>
          <PageSubtitle>
            Create and manage consent templates with customizable branding
          </PageSubtitle>
        </PageTitleGroup>
        <ActionButtons>
          <SecondaryButton onClick={() => navigate('/admin/template-library')}>
            <span>📚</span> Import from Library
          </SecondaryButton>
          <PrimaryButton onClick={handleCreateTemplate}>
            <span>+</span> Create Template
          </PrimaryButton>
        </ActionButtons>
      </PageHeader>

      <StatsRowLocal>
        <StatPillLocal>
          <span className="value">{templateList.length}</span>
          <span className="label">Total</span>
        </StatPillLocal>
        <StatPillLocal>
          <span className="value">{templateList.filter(t => t.status === 'ACTIVE').length}</span>
          <span className="label">Active</span>
        </StatPillLocal>
        <StatPillLocal>
          <span className="value">{templateList.filter(t => t.status === 'DRAFT').length}</span>
          <span className="label">Draft</span>
        </StatPillLocal>
        <StatPillLocal>
          <span className="value">{projects.length}</span>
          <span className="label">Projects</span>
        </StatPillLocal>
      </StatsRowLocal>

      <FilterGroup style={{ marginBottom: '24px' }}>
        <SearchInput
          type="text"
          placeholder="🔍 Search templates..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSearchQuery(e.target.value)}
        />
        <SelectInput value={statusFilter} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="DRAFT">Draft</option>
          <option value="DISABLED">Disabled</option>
          <option value="ARCHIVED">Archived</option>
        </SelectInput>
      </FilterGroup>

      {error && <AlertMessage $variant="error">❌ {error}</AlertMessage>}
      {success && <AlertMessage $variant="success">✅ {success}</AlertMessage>}

      {filteredTemplates.length === 0 ? (
        <EmptyStateContainer>
          <EmptyStateIcon>📋</EmptyStateIcon>
          <EmptyStateTitle>No Templates Found</EmptyStateTitle>
          <EmptyStateText>Create your first consent template to get started</EmptyStateText>
          <PrimaryButton onClick={handleCreateTemplate} style={{ marginTop: '16px' }}>
            Create Template
          </PrimaryButton>
        </EmptyStateContainer>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Template</Th>
              <Th>Project</Th>
              <Th>Data Collector</Th>
              <Th>Data Categories</Th>
              <Th>Version</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredTemplates.map((template) => (
              <Tr key={template.templateId}>
                <Td>
                  <div>
                    <TemplateNameCell>{template.name || template.templateName}</TemplateNameCell>
                    {template.description && (
                      <TemplateDescriptionCell>
                        {template.description.substring(0, 60)}...
                      </TemplateDescriptionCell>
                    )}
                  </div>
                </Td>
                <Td>{getProjectName(template.projectId)}</Td>
                <Td>{getDataCollectorName(template.dataCollectorId)}</Td>
                <Td>
                  <DataCategoriesList>
                    {(template.dataCategories || []).slice(0, 2).map((cat, i) => (
                      <CategoryTag key={i}>{cat}</CategoryTag>
                    ))}
                    {(template.dataCategories || []).length > 2 && (
                      <CategoryTag>+{template.dataCategories!.length - 2}</CategoryTag>
                    )}
                  </DataCategoriesList>
                </Td>
                <Td>v{template.version || '1'}</Td>
                <Td>
                  <StatusBadge $status={template.status || 'ACTIVE'}>{template.status || 'ACTIVE'}</StatusBadge>
                </Td>
                <Td>
                  <ActionButtons>
                    <IconButton $variant="view" onClick={() => handleViewTemplate(template)}>
                      👁️ View
                    </IconButton>
                    <IconButton $variant="edit" onClick={() => handleEditTemplate(template)}>
                      ✏️ Edit
                    </IconButton>
                    <IconButton onClick={() => handleDuplicateTemplate(template)} title="Duplicate template">
                      📋 Copy
                    </IconButton>
                    <IconButton onClick={() => handleOpenSaveToLibrary(template)} title="Save to Library">
                      📚 Library
                    </IconButton>
                    <IconButton
                      $variant={(template.status || 'ACTIVE') === 'ACTIVE' ? 'danger' : 'success'}
                      onClick={() => handleToggleStatus(template)}
                    >
                      {(template.status || 'ACTIVE') === 'ACTIVE' ? '⏸️ Pause' : '▶️ Activate'}
                    </IconButton>
                  </ActionButtons>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Template Builder Modal */}
      <FullPageModal $show={showBuilder}>
        <FullPageHeader>
          <PageTitle>{editingTemplate ? '✏️ Edit Template' : '➕ Create Template'}</PageTitle>
          <SecondaryButton onClick={() => setShowBuilder(false)}>
            ✕ Close
          </SecondaryButton>
        </FullPageHeader>
        <FullPageBody>
          <TemplateBuilder
            template={editingTemplate}
            projects={projects}
            dataCollectors={dataCollectors}
            onSave={handleSaveTemplate}
            onCancel={() => setShowBuilder(false)}
          />
        </FullPageBody>
      </FullPageModal>

      {/* View Template Modal */}
      <ViewModal
        show={showViewModal}
        onClose={() => { setShowViewModal(false); setViewModalTab('details'); }}
        title={viewingTemplate?.name || 'Template Details'}
        icon="📋"
        width="1000px"
        footer={
          <>
            <SecondaryButton onClick={() => { setShowViewModal(false); setViewModalTab('details'); }}>Close</SecondaryButton>
            <PrimaryButton onClick={() => { setShowViewModal(false); setViewModalTab('details'); handleEditTemplate(viewingTemplate!); }}>
              ✏️ Edit Template
            </PrimaryButton>
          </>
        }
      >
        {viewingTemplate && (
          <>
            <TabContainer>
              <Tab $active={viewModalTab === 'details'} onClick={() => setViewModalTab('details')}>
                📋 Details
              </Tab>
              <Tab $active={viewModalTab === 'preview'} onClick={() => setViewModalTab('preview')}>
                👁️ Live Preview
              </Tab>
            </TabContainer>
            
            {viewModalTab === 'details' && (
              <>
                <Section>
                  <SectionTitle>📌 Basic Information</SectionTitle>
                  <InfoGrid>
                    <InfoCard>
                      <InfoLabel>Template ID</InfoLabel>
                      <InfoValue $mono>{viewingTemplate.templateId}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Status</InfoLabel>
                      <InfoValue>
                        <Badge $variant={viewingTemplate.status === 'ACTIVE' ? 'success' : viewingTemplate.status === 'DRAFT' ? 'warning' : 'default'}>
                          {viewingTemplate.status}
                        </Badge>
                      </InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Project</InfoLabel>
                      <InfoValue>{getProjectName(viewingTemplate.projectId)}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Data Collector</InfoLabel>
                      <InfoValue>{getDataCollectorName(viewingTemplate.dataCollectorId)}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Version</InfoLabel>
                      <InfoValue>v{viewingTemplate.version || '1'}</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Created</InfoLabel>
                      <InfoValue>
                        {viewingTemplate.createdAt ? new Date(viewingTemplate.createdAt).toLocaleString() : 'N/A'}
                      </InfoValue>
                    </InfoCard>
                  </InfoGrid>
                </Section>

                {viewingTemplate.description && (
                  <Section>
                    <SectionTitle>📝 Description</SectionTitle>
                    <DescriptionText>{viewingTemplate.description}</DescriptionText>
                  </Section>
                )}

                {viewingTemplate.dataCategories && viewingTemplate.dataCategories.length > 0 && (
                  <Section>
                    <SectionTitle>📊 Data Categories</SectionTitle>
                    <TagList>
                      {viewingTemplate.dataCategories.map((cat, i) => (
                        <Tag key={i}>{cat}</Tag>
                      ))}
                    </TagList>
                  </Section>
                )}

                {viewingTemplate.consentText && (
                  <Section>
                    <SectionTitle>📄 Consent Content</SectionTitle>
                    <ContentPreview dangerouslySetInnerHTML={{ __html: viewingTemplate.consentText }} />
                  </Section>
                )}

                <Section>
                  <SectionTitle>⚙️ Settings</SectionTitle>
                  <InfoGrid>
                    <InfoCard>
                      <InfoLabel>Consent Validity</InfoLabel>
                      <InfoValue>{viewingTemplate.expiryDays || 365} days</InfoValue>
                    </InfoCard>
                    <InfoCard>
                      <InfoLabel>Require Reconsent</InfoLabel>
                      <InfoValue>{viewingTemplate.requireReconsent ? 'Yes' : 'No'}</InfoValue>
                    </InfoCard>
                  </InfoGrid>
                </Section>

                {viewingTemplate.evidenceTypes && viewingTemplate.evidenceTypes.length > 0 && (
                  <Section>
                    <SectionTitle>🔐 Evidence Types</SectionTitle>
                    <TagList>
                      {viewingTemplate.evidenceTypes.map((type, i) => (
                        <Tag key={i}>
                          {type === 'signature' && '✍️ '}
                          {type === 'phone_otp' && '📱 '}
                          {type === 'email_otp' && '📧 '}
                          {type === 'video' && '🎥 '}
                          {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                        </Tag>
                      ))}
                    </TagList>
                  </Section>
                )}

                {(viewingTemplate.activeConsentCount !== undefined || viewingTemplate.totalConsentCount !== undefined) && (
                  <Section>
                    <SectionTitle>📈 Statistics</SectionTitle>
                    <InfoGrid>
                      <InfoCard>
                        <InfoLabel>Active Consents</InfoLabel>
                        <InfoValue>{viewingTemplate.activeConsentCount || 0}</InfoValue>
                      </InfoCard>
                      <InfoCard>
                        <InfoLabel>Total Consents</InfoLabel>
                        <InfoValue>{viewingTemplate.totalConsentCount || 0}</InfoValue>
                      </InfoCard>
                    </InfoGrid>
                  </Section>
                )}
              </>
            )}
            
            {viewModalTab === 'preview' && (
              <DevicePreviewFrame defaultDevice="mobile" maxHeight={550}>
                <TemplatePreview
                  consentText={viewingTemplate.consentText || ''}
                  templateName={viewingTemplate.name}
                  description={viewingTemplate.description}
                  dataCategories={viewingTemplate.dataCategories}
                  expiryDays={viewingTemplate.expiryDays}
                  evidenceTypes={viewingTemplate.evidenceTypes}
                />
              </DevicePreviewFrame>
            )}
          </>
        )}
      </ViewModal>

      {/* Save to Library Modal */}
      <ViewModal
        show={showSaveToLibrary}
        onClose={() => setShowSaveToLibrary(false)}
        title="Save to Template Library"
        icon="📚"
        width="600px"
        footer={
          <>
            <SecondaryButton onClick={() => setShowSaveToLibrary(false)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSaveToLibrary} disabled={savingToLibrary}>
              {savingToLibrary ? '⏳ Saving...' : '📚 Save to Library'}
            </PrimaryButton>
          </>
        }
      >
        <SaveToLibraryContainer>
          <SaveToLibraryInfo>
            Share this template with the community by adding it to the Template Library.
          </SaveToLibraryInfo>
          
          <FormGridContainer>
            <FormFieldContainer>
              <FormFieldLabel>
                Regulation *
              </FormFieldLabel>
              <SelectInput 
                value={saveToLibraryForm.regulation}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSaveToLibraryForm(f => ({ ...f, regulation: e.target.value }))}
                style={{ width: '100%' }}
              >
                <option value="DPDP">DPDP (India)</option>
                <option value="GDPR">GDPR (EU)</option>
                <option value="CCPA">CCPA (California)</option>
                <option value="POPIA">POPIA (South Africa)</option>
                <option value="PDPA_SG">PDPA (Singapore)</option>
                <option value="PDPA_TH">PDPA (Thailand)</option>
                <option value="LGPD">LGPD (Brazil)</option>
              </SelectInput>
            </FormFieldContainer>
            
            <FormFieldContainer>
              <FormFieldLabel>
                Geography *
              </FormFieldLabel>
              <SelectInput 
                value={saveToLibraryForm.geography}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSaveToLibraryForm(f => ({ ...f, geography: e.target.value }))}
                style={{ width: '100%' }}
              >
                <option value="INDIA">India</option>
                <option value="EU">European Union</option>
                <option value="USA">United States</option>
                <option value="UK">United Kingdom</option>
                <option value="SOUTH_AFRICA">South Africa</option>
                <option value="SINGAPORE">Singapore</option>
                <option value="THAILAND">Thailand</option>
                <option value="BRAZIL">Brazil</option>
                <option value="AUSTRALIA">Australia</option>
              </SelectInput>
            </FormFieldContainer>
          </FormGridContainer>
          
          <FormFieldContainer>
            <FormFieldLabel>
              Category *
            </FormFieldLabel>
            <SelectInput 
              value={saveToLibraryForm.category}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSaveToLibraryForm(f => ({ ...f, category: e.target.value }))}
              style={{ width: '100%' }}
            >
              <option value="HEALTHCARE">Healthcare & Medical</option>
              <option value="FINANCIAL">Financial Services</option>
              <option value="MARKETING">Marketing & Advertising</option>
              <option value="EMPLOYMENT">Employment & HR</option>
              <option value="ECOMMERCE">E-Commerce & Retail</option>
              <option value="EDUCATION">Education</option>
              <option value="TELECOM">Telecommunications</option>
              <option value="GOVERNMENT">Government Services</option>
              <option value="GENERAL">General Purpose</option>
            </SelectInput>
          </FormFieldContainer>
          
          <FormFieldContainer>
            <FormFieldLabel>
              Template Name *
            </FormFieldLabel>
            <SearchInput 
              type="text"
              value={saveToLibraryForm.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSaveToLibraryForm(f => ({ ...f, name: e.target.value }))}
              placeholder="E.g., Healthcare Patient Consent - DPDP"
              style={{ width: '100%' }}
            />
          </FormFieldContainer>
          
          <FormFieldContainer>
            <FormFieldLabel>
              Description
            </FormFieldLabel>
            <FormTextarea 
              value={saveToLibraryForm.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSaveToLibraryForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe the template purpose and use cases..."
              style={{ minHeight: '80px' }}
            />
          </FormFieldContainer>
          
          <FormFieldContainer>
            <FormFieldLabel>
              Legal Basis (optional)
            </FormFieldLabel>
            <SearchInput 
              type="text"
              value={saveToLibraryForm.legalBasis}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSaveToLibraryForm(f => ({ ...f, legalBasis: e.target.value }))}
              placeholder="E.g., Section 6, DPDP Act 2023"
              style={{ width: '100%' }}
            />
          </FormFieldContainer>
          
          <FormFieldContainer>
            <FormFieldLabel>
              Compliance Notes (optional)
            </FormFieldLabel>
            <FormTextarea 
              value={saveToLibraryForm.complianceNotes}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setSaveToLibraryForm(f => ({ ...f, complianceNotes: e.target.value }))}
              placeholder="Any compliance guidance or important notes..."
              style={{ minHeight: '60px' }}
            />
          </FormFieldContainer>
        </SaveToLibraryContainer>
      </ViewModal>
    </AdminShell>
  );
};

export default Templates;
